import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, FileText, Target, MessageSquare, Users, Sparkles, PenTool } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteCopywriting = () => {
  const faqs = [
    {
      question: "What is lawn care website copywriting?",
      answer: "Lawn care website copywriting is writing the text on your website—headlines, service descriptions, calls-to-action, and page content. Good copy speaks to homeowners' needs and converts visitors into leads."
    },
    {
      question: "How much does lawn care website copywriting cost?",
      answer: "Freelance copywriters charge $500–$2,000+ for a full website. Per-page rates run $100–$300. Our Core Website plan at $200/month includes all copywriting—written by people who know lawn care."
    },
    {
      question: "Is copywriting included in your website subscription?",
      answer: "Yes. We write all website copy as part of the subscription. You provide your services, service areas, and unique selling points—we handle the writing, SEO optimization, and conversion focus."
    },
    {
      question: "What makes lawn care copywriting different from generic copywriting?",
      answer: "Lawn care copy needs to address specific homeowner concerns: reliability, quality of work, local coverage, and seasonal services. Generic copywriters often miss these industry-specific angles."
    },
    {
      question: "Do I need to write my own website content?",
      answer: "No. We handle all copywriting. You'll provide basic info (services offered, service areas, what makes you different) and we'll turn it into professional, conversion-focused website copy."
    },
    {
      question: "How does good copywriting help my lawn care website convert?",
      answer: "Good copy addresses homeowner concerns, builds trust quickly, and guides visitors to take action (call or request a quote). Poor copy confuses visitors or fails to differentiate you from competitors."
    },
    {
      question: "Will the copy be optimized for SEO?",
      answer: "Yes. We write copy that's both conversion-focused AND optimized for local search—proper headings, natural keyword use, and content that helps you rank for lawn care services in your area."
    },
    {
      question: "Can I request changes to the copy after it's written?",
      answer: "Absolutely. Revisions are part of the process. We'll refine copy until it accurately represents your business and sounds like you."
    },
    {
      question: "What information do you need from me to write the copy?",
      answer: "Your service list, service areas, years in business, what makes you different, any testimonials or reviews, and your preferred tone (professional, friendly, etc.). We handle the rest."
    },
    {
      question: "How long does it take to write website copy?",
      answer: "We typically complete all website copy within 3–5 business days as part of our 10–14 day website delivery. You'll review and approve before launch."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-copywriting/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website copywriting for lawn care companies included in our website subscription service."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-copywriting/#webpage",
    "name": "Lawn Care Website Copywriting | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-copywriting/",
    "description": "Professional lawn care website copywriting that converts. SEO-optimized copy included in our $200–$300/mo subscription.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-copywriting/#service",
    "name": "Lawn Care Website Copywriting",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Conversion-focused website copywriting for lawn care companies, optimized for local SEO and homeowner conversions.",
    "areaServed": "United States",
    "serviceType": "Website Copywriting"
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Copywriting | Leaf Rush"
        description="Professional lawn care website copywriting that converts. SEO-optimized copy included in our $200–$300/mo subscription."
        keywords="lawn care website copywriting, lawn care website content, lawn care website copy, lawn care content writing"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-copywriting/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-copywriting/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Included in Subscription
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website Copywriting That Converts Visitors Into Leads
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional, conversion-focused copy written by people who understand lawn care. SEO-optimized and included in your subscription.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Lawn care website copywriting means writing your website's text—headlines, service pages, CTAs. Freelancers charge $500–$2,000+. Our subscription includes all copywriting in the $200–$300/month price.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're exploring a complete website solution, visit our main{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What We Write */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What We Write for Your Lawn Care Website
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <FileText className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Homepage Copy</p>
                    <p className="text-sm text-muted-foreground">
                      Headlines, intro text, and sections that immediately communicate value and build trust.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Service Page Copy</p>
                    <p className="text-sm text-muted-foreground">
                      Dedicated copy for mowing, fertilization, aeration, cleanups—each service positioned for conversions.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <MessageSquare className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Calls-to-Action</p>
                    <p className="text-sm text-muted-foreground">
                      Button text, form intros, and prompts designed to get visitors to call or request a quote.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">About & Trust Copy</p>
                    <p className="text-sm text-muted-foreground">
                      Your story, credentials, and positioning that builds trust with first-time visitors.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Industry-Specific Copy Matters */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Lawn Care-Specific Copywriting Matters
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Speaks to Homeowners</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      We know what homeowners worry about—reliability, quality, and local coverage. Generic copy misses this.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Positions Your Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Each service (mowing, fertilization, cleanups) needs specific positioning. We know how to write it.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Optimized for Local SEO</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Copy that naturally includes location terms and service keywords to help you rank locally.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground">
                  See{" "}
                  <NavLink to="/lawn-care-website-content/" className="text-primary underline hover:no-underline">
                    lawn care website content guide
                  </NavLink>{" "}
                  for more on effective content.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Copywriting Cost Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Copywriting Cost Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Cost</th>
                      <th className="text-left py-3 px-4">Industry Knowledge</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">Included in $200–$300/mo</td>
                      <td className="py-3 px-4">Lawn care specialists</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Freelance Copywriter</td>
                      <td className="py-3 px-4">$500–$2,000+ one-time</td>
                      <td className="py-3 px-4">Usually generalist</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY (Write Yourself)</td>
                      <td className="py-3 px-4">$0 (your time)</td>
                      <td className="py-3 px-4">You know your business</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/lawn-care-website-cost/" className="text-primary underline hover:no-underline">
                  lawn care website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Lawn Care Website Copywriting
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional lawn care websites.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-content/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Content</h3>
                  <p className="text-sm text-muted-foreground">What content you need for your site.</p>
                </NavLink>
                <NavLink to="/service-pages-that-rank/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Service Pages That Rank</h3>
                  <p className="text-sm text-muted-foreground">Build service pages that rank in Google.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for Professional Website Copy?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes conversion-focused copywriting.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteCopywriting;
